//-- copyright
// GPE customization (gov.ao)
//
// One widget for the two lists a user opens this page to read - what is assigned to
// them and what they raised - with a toggle between them instead of two tables side
// by side, each half as wide as the columns need.
//
// It persists nothing. wp-embedded-table takes query properties directly and reloads
// when they change, so switching tabs is a matter of handing it the other set: there
// is no saved query behind the widget, and so nothing to keep in step with it, clean
// up after it, or represent over the API.
//++

import {
  ChangeDetectionStrategy,
  Component,
  Injector,
} from '@angular/core';
import { AbstractWidgetComponent } from 'core-app/shared/components/grids/widgets/abstract-widget.component';
import { WorkPackageTableConfiguration } from 'core-app/features/work-packages/components/wp-table/wp-table-configuration';
import { I18nService } from 'core-app/core/i18n/i18n.service';
import { ApiV3FilterBuilder } from 'core-app/shared/helpers/api-v3/api-v3-filter-builder';
import {
  WorkPackageIsolatedQuerySpaceDirective,
} from 'core-app/features/work-packages/directives/query-space/wp-isolated-query-space.directive';

export type MyWorkPackagesTabId = 'assigned'|'created';

interface MyWorkPackagesTab {
  id:MyWorkPackagesTabId;
  label:string;
  // Held rather than derived on read: wp-embedded-table reloads whenever the object
  // identity of its queryProps changes, so a getter building a fresh one on every
  // change detection pass would put the table in a reload loop.
  queryProps:Record<string, unknown>;
}

@Component({
  selector: 'widget-wp-toggle-table',
  templateUrl: './wp-toggle-table.component.html',
  styleUrls: ['./wp-toggle-table.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [WorkPackageIsolatedQuerySpaceDirective],
})
export class WidgetWpToggleTableComponent extends AbstractWidgetComponent {
  public readonly tabs:MyWorkPackagesTab[];

  public selected:MyWorkPackagesTab;

  public configuration:Partial<WorkPackageTableConfiguration> = {
    actionsColumnEnabled: false,
    columnMenuEnabled: false,
    hierarchyToggleEnabled: true,
    contextMenuEnabled: false,
  };

  public text = {
    tablist: this.i18n.t('js.grid.widgets.work_packages_toggle.title'),
  };

  constructor(
    protected i18n:I18nService,
    protected readonly injector:Injector,
  ) {
    super(i18n, injector);

    this.tabs = [
      this.buildTab('assigned', 'assignee'),
      this.buildTab('created', 'author'),
    ];
    this.selected = this.tabs[0];
  }

  public select(tab:MyWorkPackagesTab):void {
    this.selected = tab;
  }

  // Only open work packages, and the same four columns the two widgets this one
  // replaces showed.
  private buildTab(id:MyWorkPackagesTabId, filterName:string):MyWorkPackagesTab {
    const filters = new ApiV3FilterBuilder();
    filters.add(filterName, '=', ['me']);
    filters.add('status', 'o', []);

    return {
      id,
      label: this.i18n.t(`js.grid.widgets.work_packages_toggle.${id}`),
      queryProps: {
        'columns[]': ['id', 'project', 'type', 'subject'],
        filters: filters.toJson(),
      },
    };
  }
}
